// frontend/src/pages/PedidosPage.tsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFilterStore } from '../../stores/filterStore';
import { fetchOrders, fetchSituations, fetchOrderDetails, fetchOrderEvolution, fetchSegments, fetchLinhas, Order, Situation, Segmento, Linha, OrderKpis, PaginationInfo, EvolutionData } from './services/ordersAPI';
import {
    DateNavigator,
    formatLocalDate,
    getWeekRange,
    getYearRange,
} from '../../components/DateNavigator/DateNavigator';
import navStyles from '../../components/DateNavigator/DateNavigator.module.css';
import { useAuthStore } from '../../stores/authStore';
import PedidosPageSkeleton from './PedidosPageSkeleton';
import KpiCard from './components/Pedidos/KpiCard';
import OrderCard from './components/Pedidos/OrderCard';
import OrderDetailModal from './components/Pedidos/OrderDetailModal';
import CreateOrderModal from './components/Pedidos/CreateOrderModal';
import OrderEvolutionChart from './components/Pedidos/OrderEvolutionChart';
import styles from './PedidosPage.module.css';
import { CarteiraSelector } from '../../components/CarteiraSelector/CarteiraSelector';
import CustomSelect from '../../components/CustomSelect';
import ModernDashboardHeader from '../../components/ModernDashboardHeader/ModernDashboardHeader';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AddIcon from '@mui/icons-material/Add';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import StraightenIcon from '@mui/icons-material/Straighten';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import BarChartIcon from '@mui/icons-material/BarChart';

const HighlightText: React.FC<{ text: string | React.ReactNode; highlight: string }> = ({ text, highlight }) => {
    if (!highlight.trim() || typeof text !== 'string') {
        return <span>{text}</span>;
    }
    const regex = new RegExp(`(${highlight})`, 'gi');
    const parts = text.split(regex);
    return (
        <span>
            {parts.map((part, i) =>
                regex.test(part) ? (
                    <span key={i} className={styles.highlight}>{part}</span>
                ) : (
                    <span key={i}>{part}</span>
                )
            )}
        </span>
    );
};

// Cache em memória local para evitar Skeleton ao trocar de aba
let moduleCachedOrders: Order[] | null = null;
let moduleCachedKpis: OrderKpis | null = null;
let moduleCachedPagination: PaginationInfo | null = null;

const PedidosPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { selectedCarteiraId } = useFilterStore();

    // DateNavigator states
    const [periodo, setPeriodo] = useState<'anual' | 'mensal' | 'semanal' | 'custom'>('mensal');
    const [dataRef, setDataRef] = useState<string>(() => formatLocalDate(new Date()));
    const [dataInicio, setDataInicio] = useState<string>('');
    const [dataFim, setDataFim] = useState<string>('');

    const getFiscalMonthRange = (dateRef: string): { start: Date; end: Date } => {
        const d = new Date(dateRef + 'T12:00:00');
        const year = d.getFullYear();
        const month = d.getMonth();
        return {
            start: new Date(year, month - 1, 26, 0, 0, 0, 0),
            end: new Date(year, month, 25, 23, 59, 59, 999),
        };
    };

    const getCalendarMonthRange = (dateRef: string): { start: Date; end: Date } => {
        const d = new Date(dateRef + 'T12:00:00');
        const year = d.getFullYear();
        const month = d.getMonth();
        return {
            start: new Date(year, month, 1, 0, 0, 0, 0),
            end: new Date(year, month + 1, 0, 23, 59, 59, 999),
        };
    };

    const { role } = useAuthStore();
    const isDirectorOrSupervisor = role === 'diretor' || role === 'supervisor';
    const isSupervisorLevel = role === 'supervisor' || role === 'admin' || role === 'diretor';
    const isFiscal = !(isSupervisorLevel && !selectedCarteiraId);

    const { startDate, endDate } = useMemo(() => {
        if (periodo === 'custom') {
            return { startDate: dataInicio, endDate: dataFim };
        }
        const dRef = dataRef || formatLocalDate(new Date());
        let range: { start: Date; end: Date };
        if (periodo === 'mensal') {
            range = isFiscal ? getFiscalMonthRange(dRef) : getCalendarMonthRange(dRef);
        } else if (periodo === 'semanal') {
            range = getWeekRange(dRef);
        } else {
            range = getYearRange(dRef);
        }
        return {
            startDate: formatLocalDate(range.start),
            endDate: formatLocalDate(range.end),
        };
    }, [periodo, dataRef, dataInicio, dataFim, isFiscal]);
    
    const [orders, setOrders] = useState<Order[]>(moduleCachedOrders || []);
    const [kpis, setKpis] = useState<OrderKpis | null>(moduleCachedKpis);
    const [pagination, setPagination] = useState<PaginationInfo | null>(moduleCachedPagination);
    const [loading, setLoading] = useState<boolean>(!moduleCachedOrders);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
    const [situations, setSituations] = useState<Situation[]>([]);
    const [segments, setSegments] = useState<Segmento[]>([]);
    const [linhas, setLinhas] = useState<Linha[]>([]);
    const [selectedStatus, setSelectedStatus] = useState<string>('');
    const [selectedTipoPedido, setSelectedTipoPedido] = useState<string>('');
    const [selectedSegmento, setSelectedSegmento] = useState<string>('');
    const [selectedLinha, setSelectedLinha] = useState<string>('');
    const itemsPerPage = 15;

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const [evolutionData, setEvolutionData] = useState<EvolutionData[]>([]);
    const [isChartPopoverOpen, setIsChartPopoverOpen] = useState<boolean>(false);

    const tipoPedidoOptions = [
        "Pedido",
        "Amostra",
        "Cotação",
        "Devolução",
        "Remessa CA"
    ];

    if (isDirectorOrSupervisor) {
        tipoPedidoOptions.push("Bonificação");
    }

    // Lógica para abrir pedido via URL (Notificações)
    useEffect(() => {
        const orderIdParam = searchParams.get('orderId');
        if (orderIdParam) {
            const loadOrderFromUrl = async () => {
                try {
                    const response = await fetchOrderDetails(Number(orderIdParam));
                    const fullData = response.data;
                    const mappedOrder: Order = {
                        ID: fullData.pedido.ID,
                        COD_TEOREMA: Number(fullData.pedido.COD_TEOREMA),
                        cliente: fullData.pedido.CLIENTE_NOME || 'Cliente Desconhecido',
                        TIPO_PEDIDO: '',
                        EMISSAO: fullData.pedido.EMISSAO,
                        PREVISAO: fullData.pedido.PREVISAO,
                        SAIDA: null,
                        NOTA_FISCAL: fullData.pedido.DOCUMENTO,
                        SITUACAO: String(fullData.pedido.SITUAÇÃO)
                    };
                    setSelectedOrder(mappedOrder);
                    setIsModalOpen(true);
                    setSearchParams(params => {
                        params.delete('orderId');
                        return params;
                    });
                } catch (err) {
                    console.error("Erro ao abrir pedido via link:", err);
                }
            };
            loadOrderFromUrl();
        }
    }, [searchParams, setSearchParams]);

    const handleCardClick = (order: Order) => {
        setSelectedOrder(order);
        setIsModalOpen(true);
    };

    const handleOrderCreated = async (orderId: number) => {
        setIsCreateModalOpen(false);
        setLoading(true);
        try {
            const response = await fetchOrderDetails(orderId);
            const fullData = response.data;
            const mappedOrder: Order = {
                ID: fullData.pedido.ID,
                COD_TEOREMA: Number(fullData.pedido.COD_TEOREMA),
                cliente: fullData.pedido.CLIENTE_NOME || 'Cliente Desconhecido',
                TIPO_PEDIDO: '',
                EMISSAO: fullData.pedido.EMISSAO,
                PREVISAO: fullData.pedido.PREVISAO,
                SAIDA: null,
                NOTA_FISCAL: fullData.pedido.DOCUMENTO,
                SITUACAO: String(fullData.pedido.SITUAÇÃO)
            };
            setSelectedOrder(mappedOrder);
            setIsModalOpen(true);
            loadData(1); // Refresh list
        } catch (err) {
            console.error("Erro ao abrir novo pedido:", err);
            alert("Pedido criado, mas falha ao abrir detalhes.");
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedOrder(null);
    };

    const loadData = useCallback(async (page: number) => {
        if (!startDate || !endDate) return;
        try {
            setLoading(true);
            const response = await fetchOrders(startDate, endDate, page, itemsPerPage, debouncedSearchTerm, selectedStatus, selectedCarteiraId, selectedTipoPedido, undefined, selectedSegmento, selectedLinha);
            setOrders(response.data.orders);
            moduleCachedOrders = response.data.orders;
            setKpis(response.data.kpis);
            moduleCachedKpis = response.data.kpis;
            setPagination(response.data.pagination);
            moduleCachedPagination = response.data.pagination;
            setError(null);
        } catch (err) {
            setError('Falha ao carregar os pedidos.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, debouncedSearchTerm, selectedStatus, selectedCarteiraId, selectedTipoPedido, selectedSegmento, selectedLinha]);

    // Carregar dados de evolução
    useEffect(() => {
        const loadEvolution = async () => {
            if (!startDate || !endDate) return;
            try {
                const response = await fetchOrderEvolution(
                    startDate, endDate,
                    selectedStatus || undefined,
                    selectedCarteiraId || undefined,
                    selectedTipoPedido || undefined,
                    undefined,
                    selectedSegmento || undefined,
                    selectedLinha || undefined
                );
                setEvolutionData(response.data);
            } catch (err) {
                console.error("Erro ao carregar evolução:", err);
            }
        };
        loadEvolution();
    }, [startDate, endDate, selectedStatus, selectedCarteiraId, selectedTipoPedido, selectedSegmento, selectedLinha]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1);
        }, 500);
        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    useEffect(() => {
        loadData(currentPage);
    }, [loadData, currentPage]);

    const paginate = (pageNumber: number) => {
        if (pageNumber !== currentPage) {
            setCurrentPage(pageNumber);
        }
    };

    useEffect(() => {
        const loadSituations = async () => {
            if (!startDate || !endDate) return;
            try {
                const response = await fetchSituations(startDate, endDate, selectedCarteiraId, selectedTipoPedido, undefined, selectedSegmento, selectedLinha);
                setSituations(response.data);
            } catch (err) {
                console.error("Falha ao carregar situações", err);
            }
        };
        loadSituations();
    }, [startDate, endDate, selectedCarteiraId, selectedTipoPedido, selectedSegmento, selectedLinha]);

    useEffect(() => {
        const loadSegments = async () => {
            if (isDirectorOrSupervisor) {
                try {
                    const response = await fetchSegments();
                    setSegments(response.data);
                } catch (err) {
                    console.error("Falha ao carregar segmentos", err);
                }
            }
        };
        loadSegments();
    }, [isDirectorOrSupervisor]);

    useEffect(() => {
        const loadLinhas = async () => {
            try {
                const response = await fetchLinhas();
                setLinhas(response.data);
            } catch (err) {
                console.error("Falha ao carregar linhas", err);
            }
        };
        loadLinhas();
    }, []);

    const handlePreviousPage = () => { if (currentPage > 1) setCurrentPage(currentPage - 1); };
    const handleNextPage = () => { if (pagination && currentPage < pagination.totalPages) setCurrentPage(currentPage + 1); };

    const getPaginationButtons = () => {
        if (!pagination) return [];
        const { totalPages, currentPage } = pagination;
        const pageNumbers: (number | string)[] = [];
        const maxButtons = 5;
        const halfMax = Math.floor(maxButtons / 2);

        if (totalPages <= maxButtons) {
            for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
        } else {
            pageNumbers.push(1);
            if (currentPage > halfMax + 2) pageNumbers.push('...');
            let startPage = Math.max(2, currentPage - halfMax);
            let endPage = Math.min(totalPages - 1, currentPage + halfMax);
            if (currentPage <= halfMax + 1) endPage = maxButtons - 1;
            if (currentPage >= totalPages - halfMax) startPage = totalPages - maxButtons + 2;
            for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
            if (currentPage < totalPages - halfMax - 1) pageNumbers.push('...');
            pageNumbers.push(totalPages);
        }
        return pageNumbers;
    };

    const formatCurrency = (value: number) =>
        value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact', compactDisplay: 'short' } as any);

    return (
        <div className={styles.pageContainer}>

            {/* Filter Bar (Combo Panel: Controles + KPIs) */}
            <div className={styles.filterBar}>
                <div className={styles.filterTopRow}>
                    <div className={styles.filterGroup}>
                        <div className={navStyles.periodContainer}>
                            {(['anual', 'mensal', 'semanal', 'custom'] as const).map((p) => (
                                <button
                                    key={p}
                                    onClick={() => {
                                        setPeriodo(p);
                                        setCurrentPage(1);
                                    }}
                                    className={`${navStyles.periodButton} ${
                                        periodo === p ? navStyles.periodButtonActive : ''
                                    }`}
                                >
                                    {p === 'custom' ? 'Personalizado' : p}
                                </button>
                            ))}
                        </div>

                        <DateNavigator
                            label=""
                            periodo={periodo}
                            dataRef={dataRef}
                            dataInicio={dataInicio}
                            dataFim={dataFim}
                            onChange={(vals) => {
                                if (vals.periodo) setPeriodo(vals.periodo);
                                if (vals.dataRef) setDataRef(vals.dataRef);
                                if (vals.dataInicio) setDataInicio(vals.dataInicio);
                                if (vals.dataFim) setDataFim(vals.dataFim);
                                setCurrentPage(1);
                            }}
                        />

                        <div className={styles.chartPopoverWrapper}>
                            <button
                                type="button"
                                className={styles.chartToggleButton}
                                onClick={() => setIsChartPopoverOpen(!isChartPopoverOpen)}
                            >
                                <BarChartIcon fontSize="small" />
                                Acompanhamento
                            </button>
                            {isChartPopoverOpen && (
                                <div className={styles.chartPopover}>
                                    <div className={styles.chartPopoverHeader}>
                                        <h3 className={styles.chartPopoverTitle}>Acompanhamento Mensal</h3>
                                        <button
                                            type="button"
                                            className={styles.chartPopoverCloseBtn}
                                            onClick={() => setIsChartPopoverOpen(false)}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </button>
                                    </div>
                                    <div className={styles.chartPopoverBody}>
                                        <OrderEvolutionChart data={evolutionData} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <ModernDashboardHeader variant="minimal" />
                </div>
                {kpis && (
                    <div className={styles.kpiBottomRow}>
                        <span className={styles.kpiMicroItem}>
                            <ReceiptLongIcon style={{ fontSize: 16, color: '#64748b' }} />
                            <span className={styles.kpiMicroLabel}>Pedidos:</span>
                            <span className={styles.kpiMicroValue}>{kpis.totalPedidos.toLocaleString('pt-BR')}</span>
                        </span>
                        <span className={styles.kpiMicroItem}>
                            <MonetizationOnIcon style={{ fontSize: 16, color: '#16a34a' }} />
                            <span className={styles.kpiMicroLabel}>Faturamento:</span>
                            <span className={styles.kpiMicroValue}>{formatCurrency(Number(kpis.faturamentoTotal || 0))}</span>
                        </span>
                        <span className={styles.kpiMicroItem}>
                            <StraightenIcon style={{ fontSize: 16, color: '#2563eb' }} />
                            <span className={styles.kpiMicroLabel}>Pares:</span>
                            <span className={styles.kpiMicroValue}>{Number(kpis.totalPares || 0).toLocaleString('pt-BR')}</span>
                        </span>
                    </div>
                )}
            </div>

            {/* ORDERS TABLE */}
            <div className={styles.tableContainer}>
                <div className={styles.tableToolbar}>
                    <div className={styles.tableSearchBar}>
                        <SearchIcon className={styles.tableSearchIcon} />
                        <input
                            type="text"
                            placeholder="Buscar por cód, cliente ou NF..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={styles.tableSearchInput}
                        />
                        {searchTerm && (
                            <ClearIcon
                                className={styles.tableSearchClear}
                                onClick={() => setSearchTerm('')}
                            />
                        )}
                    </div>

                    <div className={styles.tableFilters}>
                        {isDirectorOrSupervisor && <CarteiraSelector />}
                        {isDirectorOrSupervisor && (
                            <CustomSelect
                                id="segmento-select" label="Segmento"
                                value={selectedSegmento}
                                onChange={(e) => {
                                    setSelectedSegmento(e.target.value);
                                    setCurrentPage(1);
                                }}
                                placeholder="Segmento"
                                options={[
                                    { value: '', label: 'Todos' },
                                    ...segments.map((s) => ({ value: String(s.ID), label: s.DESCRIÇÃO }))
                                ]}
                            />
                        )}
                        <CustomSelect
                            id="linha-select" label="Linha"
                            value={selectedLinha}
                            onChange={(e) => { setSelectedLinha(e.target.value); setCurrentPage(1); }}
                            placeholder="Linha"
                            options={[{ value: '', label: 'Todas' }, ...linhas.map((l) => ({ value: l.DESCRIÇÃO, label: l.DESCRIÇÃO }))]}
                        />
                        <CustomSelect
                            id="tipo-select" label="Tipo"
                            value={selectedTipoPedido}
                            onChange={(e) => { setSelectedTipoPedido(e.target.value); setCurrentPage(1); }}
                            placeholder="Tipo"
                            options={[{ value: '', label: 'Todos' }, ...tipoPedidoOptions.map((t) => ({ value: t, label: t }))]}
                        />
                        <CustomSelect
                            id="situacao-select" label="Situação"
                            value={selectedStatus}
                            onChange={(e) => { setSelectedStatus(e.target.value); setCurrentPage(1); }}
                            placeholder="Situação"
                            options={[
                                { value: '', label: 'Todas' },
                                ...situations.map((s) => ({ value: String(s.ID), label: s.DESCRIÇÃO, count: s.total }))
                            ]}
                        />
                        {!isDirectorOrSupervisor && (
                            <button className={styles.newOrderButton} onClick={() => setIsCreateModalOpen(true)}>
                                <AddIcon fontSize="small" /> Novo Pedido
                            </button>
                        )}
                    </div>
                </div>
                {loading ? (
                    <>
                        <table className={styles.ordersTable}>
                            <thead>
                                <tr>
                                    <th style={{ width: '10%' }}>Situação</th>
                                    <th>ID</th>
                                    <th style={{ width: '5%', whiteSpace: 'nowrap' }}>Cód. Pedido</th>
                                    <th>Tipo</th>
                                    <th>Cliente</th>
                                    {isSupervisorLevel && <th>Vendedor/Suporte</th>}
                                    <th>Nota Fiscal</th>
                                    <th>Operação fiscal</th>
                                    <th>Emissão</th>
                                    <th>Previsão</th>
                                    <th>Saída</th>
                                    <th style={{ textAlign: 'right' }}>Total</th>
                                </tr>
                            </thead>
                        </table>
                        <PedidosPageSkeleton />
                    </>
                ) : error ? (
                    <p className={styles.error}>{error}</p>
                ) : (
                    <table className={styles.ordersTable}>
                        <thead>
                            <tr>
                                <th style={{ width: '10%' }}>Situação</th>
                                <th>ID</th>
                                <th style={{ width: '5%', whiteSpace: 'nowrap' }}>Cód. Pedido</th>
                                <th>Tipo</th>
                                <th>Cliente</th>
                                {isSupervisorLevel && <th>Vendedor/Suporte</th>}
                                <th>Nota Fiscal</th>
                                <th>Operação fiscal</th>
                                <th>Emissão</th>
                                <th>Previsão</th>
                                <th>Saída</th>
                                <th style={{ textAlign: 'right' }}>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((order: Order) => {
                                const emissao = order.EMISSAO ? new Date(order.EMISSAO).toLocaleDateString('pt-BR') : 'N/A';
                                const previsao = order.PREVISAO ? new Date(order.PREVISAO).toLocaleDateString('pt-BR') : 'N/A';
                                const saida = order.SAIDA ? new Date(order.SAIDA).toLocaleDateString('pt-BR') : '---';
                                const statusFormatted = order.SITUACAO.toLowerCase().replace(/\s+/g, '').replace(/ç/g, 'c').replace(/ã/g, 'a');
                                let statusClass = styles.statusDefault;
                                switch (statusFormatted) {
                                    case 'faturado': statusClass = styles.statusFaturado; break;
                                    case 'aberto': statusClass = styles.statusAberto; break;
                                    case 'cotacao': statusClass = styles.statusCotacao; break;
                                    case 'bloqueado': statusClass = styles.statusBloqueado; break;
                                    case 'fechamento': statusClass = styles.statusFechamento; break;
                                    case 'conferencia': statusClass = styles.statusConferencia; break;
                                    case 'aguardandolancamento': statusClass = styles.statusAguardandoLancamento; break;
                                    case 'aguardandoliberacao': statusClass = styles.statusAguardandoLiberacao; break;
                                    case 'aguardandoaprovacao': statusClass = styles.statusAguardandoAprovacao; break;
                                    case 'aguardandoautorizacao': statusClass = styles.statusAguardandoAutorizacao; break;
                                    case 'cancelado': statusClass = styles.statusCancelado; break;
                                    case 'digitando': statusClass = styles.statusDigitando; break;
                                }

                                return (
                                    <tr key={order.ID} onClick={() => handleCardClick(order)} className={styles.tableRow}>
                                        <td style={{ width: '10%' }}>
                                            <span className={`${styles.statusBadge} ${statusClass}`}>
                                                {order.SITUACAO}
                                            </span>
                                        </td>
                                        <td style={{fontWeight: 500}}>{order.ID}</td>
                                        <td style={{ width: '5%', whiteSpace: 'nowrap' }}><HighlightText text={String(order.COD_TEOREMA)} highlight={debouncedSearchTerm} /></td>
                                        <td>{order.TIPO_PEDIDO || 'N/A'}</td>
                                        <td className={styles.clientCell} title={order.cliente}>
                                            <HighlightText text={order.cliente} highlight={debouncedSearchTerm} />
                                        </td>
                                        {isSupervisorLevel && (
                                            <td title={order.CARTEIRA}>{order.CARTEIRA || '---'}</td>
                                        )}
                                        <td><HighlightText text={order.NOTA_FISCAL || '---'} highlight={debouncedSearchTerm} /></td>
                                        <td title={order.OPERACAO_FISCAL}>{order.OPERACAO_FISCAL || '---'}</td>
                                        <td>{emissao}</td>
                                        <td>{previsao}</td>
                                        <td>{saida}</td>
                                        <td className={styles.totalCell} style={{ textAlign: 'right' }}>
                                            {Number(order.VALOR_TOTAL || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                        </td>
                                    </tr>
                                );
                            })}
                            {orders.length === 0 && (
                                <tr>
                                    <td colSpan={isSupervisorLevel ? 11 : 10} className={styles.emptyTable}>Nenhum pedido encontrado.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {pagination && pagination.totalPages > 1 && (
                <div className={styles.pagination}>
                    <div className={styles.paginationInfo}>
                        Mostrando {(currentPage - 1) * itemsPerPage + 1} -{' '}
                        {Math.min(currentPage * itemsPerPage, pagination.totalItems)} de{' '}
                        {pagination.totalItems} pedidos
                    </div>
                    <div className={styles.pageButtons}>
                        <button
                            className={styles.pageButton}
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                        >
                            <ChevronLeftIcon fontSize="small" />
                            Anterior
                        </button>

                        <div className={styles.pageNumbers}>
                            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(
                                (page) => {
                                    if (
                                        page === 1 ||
                                        page === pagination.totalPages ||
                                        (page >= currentPage - 2 && page <= currentPage + 2)
                                    ) {
                                        return (
                                            <button
                                                key={page}
                                                onClick={() => setCurrentPage(page)}
                                                className={`${styles.pageNumber} ${
                                                    page === currentPage
                                                        ? styles.pageNumberActive
                                                        : ''
                                                }`}
                                            >
                                                {page}
                                            </button>
                                        );
                                    }
                                    if (
                                        page === currentPage - 3 ||
                                        page === currentPage + 3
                                    ) {
                                        return (
                                            <span
                                                key={page}
                                                className={styles.pageEllipsis}
                                            >
                                                ...
                                            </span>
                                        );
                                    }
                                    return null;
                                }
                            )}
                        </div>

                        <button
                            className={styles.pageButton}
                            onClick={handleNextPage}
                            disabled={currentPage === pagination.totalPages}
                        >
                            Próxima
                            <ChevronRightIcon fontSize="small" />
                        </button>
                    </div>
                </div>
            )}

            {isModalOpen && selectedOrder && (
                <OrderDetailModal
                    order={selectedOrder}
                    onClose={handleCloseModal}
                    onOrderUpdated={() => loadData(currentPage)}
                />
            )}

            {isCreateModalOpen && (
                <CreateOrderModal
                    onClose={() => setIsCreateModalOpen(false)}
                    onOrderCreated={handleOrderCreated}
                />
            )}
        </div>
    );
};

export default PedidosPage;